import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type CastMember,
  CONTENT_RATINGS,
  type ContentRating,
  type Episode,
  type Show,
  type ShowStatus,
} from '@gobingebox/shared';
import { cn } from '@/lib/cn';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Input';
import { Calendar, Check, Loader2, Play, Plus, X } from '@/components/icons';
import { ApiError } from '@/lib/api';
import { catalogApi } from '@/features/catalog/api';
import { contentApi, uploadVideo } from './contentApi';

const STATUSES: ShowStatus[] = ['upcoming', 'ongoing', 'completed'];

function castToText(cast: CastMember[]): string {
  return cast.map((c) => `${c.name} | ${c.role} | ${c.kind}`).join('\n');
}
function textToCast(text: string): CastMember[] {
  return text
    .split('\n')
    .map((l) => l.split('|').map((p) => p.trim()))
    .filter((p) => p[0])
    .map((p) => ({ name: p[0]!, role: p[1] ?? '', kind: p[2] === 'crew' ? 'crew' : 'voice' }));
}
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}

function VideoUpload({ onUploaded, label }: { onUploaded: (uid: string) => void; label: string }) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setErr(null);
    try {
      const uid = await uploadVideo(file);
      onUploaded(uid);
    } catch (error) {
      setErr(
        error instanceof ApiError && error.body.error === 'stream_not_configured'
          ? 'Enable Cloudflare Stream to upload video.'
          : 'Upload failed. Try again.',
      );
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = '';
    }
  }

  return (
    <div>
      <input ref={ref} type="file" accept="video/*" hidden onChange={onPick} />
      <Button size="sm" variant="secondary" onClick={() => ref.current?.click()} disabled={busy}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
        {label}
      </Button>
      {err && <p className="mt-1 text-xs text-danger">{err}</p>}
    </div>
  );
}

function EpisodeRow({ ep, onChanged }: { ep: Episode; onChanged: () => void }) {
  const update = useMutation({ mutationFn: (input: Parameters<typeof contentApi.updateEpisode>[1]) => contentApi.updateEpisode(ep.id, input), onSuccess: onChanged });
  const del = useMutation({ mutationFn: () => contentApi.deleteEpisode(ep.id), onSuccess: onChanged });

  return (
    <li className="card p-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-surface-raised text-sm font-bold text-ink-muted">
          {ep.number}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-ink">{ep.title}</p>
          <p className="text-xs text-ink-faint">
            {ep.streamUid ? (
              <span className="text-success">Video uploaded</span>
            ) : (
              <span>No video yet</span>
            )}
            {ep.releaseAt && ` · ${new Date(ep.releaseAt).toLocaleDateString()}`}
          </p>
        </div>
        <input
          type="datetime-local"
          defaultValue={toLocalInput(ep.releaseAt)}
          onChange={(e) =>
            update.mutate({ releaseAt: e.target.value ? new Date(e.target.value).toISOString() : null })
          }
          className="h-9 rounded-lg border border-border bg-surface-raised px-2 text-xs text-ink"
          title="Release date/time"
        />
        <VideoUpload label={ep.streamUid ? 'Replace' : 'Upload video'} onUploaded={(uid) => update.mutate({ streamUid: uid })} />
        <button
          type="button"
          onClick={() => del.mutate()}
          className="grid h-8 w-8 place-items-center rounded-full text-ink-muted hover:text-danger"
          aria-label="Delete episode"
        >
          <X size={15} />
        </button>
      </div>
    </li>
  );
}

function EpisodesPanel({ show, episodes, onChanged }: { show: Show; episodes: Episode[]; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ season: 1, number: episodes.length + 1, title: '', releaseAt: '' });
  useEffect(() => setForm((f) => ({ ...f, number: episodes.length + 1 })), [episodes.length]);

  const add = useMutation({
    mutationFn: () =>
      contentApi.addEpisode(show.id, {
        season: form.season,
        number: form.number,
        title: form.title,
        releaseAt: form.releaseAt ? new Date(form.releaseAt).toISOString() : null,
      }),
    onSuccess: () => {
      setForm((f) => ({ ...f, title: '', releaseAt: '' }));
      setOpen(false);
      onChanged();
    },
  });

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h4 className="font-display text-base font-bold text-ink">Episodes</h4>
        <Button size="sm" onClick={() => setOpen((v) => !v)}>
          <Plus size={14} /> Add episode
        </Button>
      </div>
      {open && (
        <div className="card mb-3 flex flex-wrap items-end gap-2 p-3">
          <label className="text-xs text-ink-muted">
            S
            <input type="number" min={1} value={form.season} onChange={(e) => setForm({ ...form, season: Number(e.target.value) })} className="ml-1 h-9 w-14 rounded-lg border border-border bg-surface px-2 text-sm text-ink" />
          </label>
          <label className="text-xs text-ink-muted">
            E
            <input type="number" min={1} value={form.number} onChange={(e) => setForm({ ...form, number: Number(e.target.value) })} className="ml-1 h-9 w-16 rounded-lg border border-border bg-surface px-2 text-sm text-ink" />
          </label>
          <input placeholder="Episode title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-9 flex-1 rounded-lg border border-border bg-surface px-2 text-sm text-ink" />
          <input type="datetime-local" value={form.releaseAt} onChange={(e) => setForm({ ...form, releaseAt: e.target.value })} className="h-9 rounded-lg border border-border bg-surface px-2 text-xs text-ink" />
          <Button size="sm" onClick={() => add.mutate()} disabled={!form.title || add.isPending}>
            Add
          </Button>
        </div>
      )}
      {episodes.length === 0 ? (
        <p className="text-sm text-ink-faint">No episodes yet.</p>
      ) : (
        <ul className="space-y-2">
          {episodes.map((ep) => (
            <EpisodeRow key={ep.id} ep={ep} onChanged={onChanged} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ShowEditor({ slug, onClose }: { slug: string | 'new'; onClose: () => void }) {
  const qc = useQueryClient();
  const isNew = slug === 'new';
  const detail = useQuery({ queryKey: ['content-show', slug], queryFn: () => contentApi.show(slug), enabled: !isNew });
  const genresQ = useQuery({ queryKey: ['genres'], queryFn: catalogApi.genres });

  const [form, setForm] = useState({
    title: '',
    description: '',
    status: 'upcoming' as ShowStatus,
    rating: '' as ContentRating | '',
    genreIds: [] as string[],
    cast: '',
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (detail.data) {
      const s = detail.data.show;
      setForm({
        title: s.title,
        description: s.description,
        status: s.status,
        rating: s.rating ?? '',
        genreIds: s.genres.map((g) => g.id),
        cast: castToText(s.cast),
      });
    }
  }, [detail.data]);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['content-shows'] });
    void qc.invalidateQueries({ queryKey: ['content-show', slug] });
    void qc.invalidateQueries({ queryKey: ['browse'] });
  };

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        title: form.title,
        description: form.description,
        status: form.status,
        rating: form.rating || null,
        genreIds: form.genreIds,
        cast: textToCast(form.cast),
      };
      if (isNew) return contentApi.createShow(payload);
      return contentApi.updateShow(detail.data!.show.id, payload);
    },
    onSuccess: (res) => {
      refresh();
      if (isNew && 'show' in res) onClose();
    },
    onError: () => setError('Could not save. Check the fields and try again.'),
  });

  const del = useMutation({
    mutationFn: () => contentApi.deleteShow(detail.data!.show.id),
    onSuccess: () => {
      refresh();
      onClose();
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.title.trim()) return setError('Title is required.');
    save.mutate();
  }

  return (
    <div className="space-y-6">
      <button type="button" onClick={onClose} className="text-sm text-ink-muted hover:text-ink">
        ← Back to all shows
      </button>

      <form className="card space-y-4 p-6" onSubmit={onSubmit}>
        {error && <Alert tone="error">{error}</Alert>}
        <Field label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <div className="space-y-1.5">
          <label htmlFor="desc" className="block text-sm font-medium text-ink">Description</label>
          <textarea id="desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-ink focus:border-brand" />
        </div>
        <div className="flex flex-wrap gap-4">
          <label className="text-sm text-ink-muted">
            Status
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as ShowStatus })} className="ml-2 h-9 rounded-lg border border-border bg-surface-raised px-2 text-sm capitalize text-ink">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="text-sm text-ink-muted">
            Rating
            <select value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value as ContentRating | '' })} className="ml-2 h-9 rounded-lg border border-border bg-surface-raised px-2 text-sm text-ink">
              <option value="">—</option>
              {CONTENT_RATINGS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </label>
        </div>
        <div>
          <p className="mb-1.5 text-sm font-medium text-ink">Genres</p>
          <div className="flex flex-wrap gap-2">
            {(genresQ.data?.genres ?? []).map((g) => {
              const on = form.genreIds.includes(g.id);
              return (
                <button
                  key={g.id}
                  type="button"
                  onClick={() =>
                    setForm((f) => ({
                      ...f,
                      genreIds: on ? f.genreIds.filter((x) => x !== g.id) : [...f.genreIds, g.id],
                    }))
                  }
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium',
                    on ? 'border-brand bg-brand/15 text-brand-soft' : 'border-border text-ink-muted',
                  )}
                >
                  {on && <Check size={12} />}
                  {g.name}
                </button>
              );
            })}
          </div>
        </div>
        <div className="space-y-1.5">
          <label htmlFor="cast" className="block text-sm font-medium text-ink">
            Cast & credits <span className="text-ink-faint">(one per line: Name | Role | voice or crew)</span>
          </label>
          <textarea id="cast" rows={3} value={form.cast} onChange={(e) => setForm({ ...form, cast: e.target.value })} placeholder="Aoi Tanaka | Rei | voice" className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-ink focus:border-brand" />
        </div>
        <div className="flex items-center gap-2">
          <Button type="submit" disabled={save.isPending}>{isNew ? 'Create show' : 'Save changes'}</Button>
          {!isNew && (
            <Button type="button" variant="danger" onClick={() => del.mutate()} disabled={del.isPending}>
              Delete show
            </Button>
          )}
        </div>
      </form>

      {!isNew && detail.data && (
        <div className="card p-6">
          <EpisodesPanel show={detail.data.show} episodes={detail.data.episodes} onChanged={refresh} />
        </div>
      )}
    </div>
  );
}

export function Content() {
  const { data, isLoading } = useQuery({ queryKey: ['content-shows'], queryFn: contentApi.shows });
  const [editing, setEditing] = useState<string | 'new' | null>(null);

  if (editing) return <ShowEditor slug={editing} onClose={() => setEditing(null)} />;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-display text-base font-bold text-ink">Shows</h3>
        <Button size="sm" onClick={() => setEditing('new')}>
          <Plus size={14} /> New show
        </Button>
      </div>
      {isLoading ? (
        <p className="text-sm text-ink-faint">Loading…</p>
      ) : (data?.shows.length ?? 0) === 0 ? (
        <p className="text-sm text-ink-muted">No shows yet. Create your first one.</p>
      ) : (
        <ul className="space-y-2">
          {data!.shows.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => setEditing(s.slug)}
                className="card flex w-full items-center gap-3 p-4 text-left transition-colors hover:bg-surface-raised"
              >
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-surface-raised text-ink-muted">
                  <Play size={16} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-ink">{s.title}</span>
                  <span className="block text-xs text-ink-faint">
                    <span className="capitalize">{s.status}</span>
                    {s.rating && ` · ${s.rating}`}
                    {` · ${s.genres.map((g) => g.name).join(', ') || 'no genres'}`}
                  </span>
                </span>
                {s.status === 'upcoming' && <Calendar size={15} className="text-brand-soft" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
